// Wallet Event Monitor component for comprehensive wallet state logging
import React, { useEffect } from 'react';
import { useWalletEventLogger } from '../../hooks/useWalletEventLogger';

interface WalletEventMonitorProps {
  enableDebugLogs?: boolean;
  flushInterval?: number;
}

export const WalletEventMonitor: React.FC<WalletEventMonitorProps> = ({
  enableDebugLogs = false,
  flushInterval = 5000
}) => {
  const {
    forceFlushLogs,
    hasPendingLogs,
    getCurrentWalletState,
    logCustomWalletEvent
  } = useWalletEventLogger({
    enablePeriodicFlush: true,
    flushInterval,
    enableDebugLogs
  });

  // Log initial wallet state on mount
  useEffect(() => {
    const initialState = getCurrentWalletState();
    
    if (enableDebugLogs) {
      console.log('[WalletEventMonitor] Initial wallet state:', initialState);
    }

    // Log initial state if any wallets are connected
    if (initialState.ethereum.connected) {
      logCustomWalletEvent('metamask', 'initial_state_connected', {
        address: initialState.ethereum.address,
        connector: initialState.ethereum.connector,
        source: 'component_mount'
      });
    }

    if (initialState.solana.connected) {
      logCustomWalletEvent('phantom', 'initial_state_connected', {
        address: initialState.solana.address,
        walletName: initialState.solana.walletName,
        source: 'component_mount'
      });
    }

    // Force flush initial logs
    setTimeout(forceFlushLogs, 1000);
  }, []);

  // Listen for custom wallet events from other parts of the app
  useEffect(() => {
    const handleCustomWalletEvent = (event: CustomEvent) => {
      const { walletType, action, data } = event.detail;
      
      if (enableDebugLogs) {
        console.log('[WalletEventMonitor] Custom wallet event:', { walletType, action, data });
      }
      
      logCustomWalletEvent(walletType, action, {
        ...data,
        source: 'custom_event',
        timestamp: Date.now()
      });
      
      // Force flush after custom events
      setTimeout(forceFlushLogs, 100);
    };

    const handleForceFlushEvent = () => {
      if (enableDebugLogs) {
        console.log('[WalletEventMonitor] Force flush event received');
      }
      forceFlushLogs();
    };

    window.addEventListener('wallet-event', handleCustomWalletEvent as EventListener);
    window.addEventListener('force-flush-logs', handleForceFlushEvent);

    return () => {
      window.removeEventListener('wallet-event', handleCustomWalletEvent as EventListener);
      window.removeEventListener('force-flush-logs', handleForceFlushEvent);
    };
  }, [logCustomWalletEvent, forceFlushLogs, enableDebugLogs]);

  // Listen for wallet connection attempts from AppKit/WalletConnect
  useEffect(() => {
    const handleWalletConnectAttempt = () => {
      if (enableDebugLogs) {
        console.log('[WalletEventMonitor] Wallet connect attempt detected');
      }
      
      logCustomWalletEvent('metamask', 'connection_attempt', {
        source: 'appkit_walletconnect',
        timestamp: Date.now(),
        userAgent: navigator.userAgent
      });
    };

    const handleWalletConnectSuccess = (event: CustomEvent) => {
      const { address, connector } = event.detail || {};
      
      if (enableDebugLogs) {
        console.log('[WalletEventMonitor] Wallet connect success:', { address, connector });
      }
      
      logCustomWalletEvent('metamask', 'connection_success', {
        address,
        connector,
        source: 'appkit_walletconnect',
        timestamp: Date.now()
      });
      
      // Force flush after successful connection
      setTimeout(forceFlushLogs, 100);
    };

    const handleWalletConnectError = (event: CustomEvent) => {
      const { error, connector } = event.detail || {};
      
      if (enableDebugLogs) {
        console.log('[WalletEventMonitor] Wallet connect error:', { error, connector });
      }
      
      logCustomWalletEvent('metamask', 'connection_error', {
        error: error?.message || 'Unknown error',
        errorCode: error?.code,
        connector,
        source: 'appkit_walletconnect',
        timestamp: Date.now()
      });
      
      // Force flush after errors
      setTimeout(forceFlushLogs, 100);
    };

    // Listen for AppKit/WalletConnect events
    window.addEventListener('wallet-connect-attempt', handleWalletConnectAttempt);
    window.addEventListener('wallet-connect-success', handleWalletConnectSuccess as EventListener);
    window.addEventListener('wallet-connect-error', handleWalletConnectError as EventListener);

    return () => {
      window.removeEventListener('wallet-connect-attempt', handleWalletConnectAttempt);
      window.removeEventListener('wallet-connect-success', handleWalletConnectSuccess as EventListener);
      window.removeEventListener('wallet-connect-error', handleWalletConnectError as EventListener);
    };
  }, [logCustomWalletEvent, forceFlushLogs, enableDebugLogs]);

  // Monitor for network changes
  useEffect(() => {
    const handleChainChanged = (chainId: string) => {
      if (enableDebugLogs) {
        console.log('[WalletEventMonitor] Chain changed:', chainId);
      }
      
      logCustomWalletEvent('metamask', 'chain_changed', {
        chainId: parseInt(chainId, 16),
        chainIdHex: chainId,
        source: 'ethereum_provider',
        timestamp: Date.now()
      });
      
      setTimeout(forceFlushLogs, 100);
    };

    const handleAccountsChanged = (accounts: string[]) => {
      if (enableDebugLogs) {
        console.log('[WalletEventMonitor] Accounts changed:', accounts);
      }
      
      logCustomWalletEvent('metamask', 'accounts_changed', {
        accounts,
        accountCount: accounts.length,
        newAddress: accounts[0] || null,
        source: 'ethereum_provider',
        timestamp: Date.now()
      });
      
      setTimeout(forceFlushLogs, 100);
    };

    // Listen for Ethereum provider events
    if (typeof window !== 'undefined' && window.ethereum) {
      const ethereum = window.ethereum as any;
      ethereum.on?.('chainChanged', handleChainChanged);
      ethereum.on?.('accountsChanged', handleAccountsChanged);

      return () => {
        if (window.ethereum) {
          const ethereum = window.ethereum as any;
          ethereum.removeListener?.('chainChanged', handleChainChanged);
          ethereum.removeListener?.('accountsChanged', handleAccountsChanged);
        }
      };
    }
  }, [logCustomWalletEvent, forceFlushLogs, enableDebugLogs]);

  // Debug info display (only in development)
  if (enableDebugLogs && process.env.NODE_ENV === 'development') {
    return (
      <div style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        background: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 9999,
        maxWidth: '300px'
      }}>
        <div>🔍 Wallet Event Monitor</div>
        <div>Pending Logs: {hasPendingLogs() ? 'Yes' : 'No'}</div>
        <div>Flush Interval: {flushInterval}ms</div>
        <button 
          onClick={forceFlushLogs}
          style={{
            marginTop: '5px',
            padding: '2px 5px',
            fontSize: '10px',
            cursor: 'pointer'
          }}
        >
          Force Flush
        </button>
      </div>
    );
  }

  // Component is invisible in production
  return null;
};

export default WalletEventMonitor;
